// utils/enhancedCheckinLocationService.js - FIXED venue detection
import { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

export class EnhancedCheckinLocationService {
  constructor() {
    this.watchId = null;
    this.isTracking = false;
    this.callbacks = new Set();
    this.lastKnownPosition = null;
    this.checkInterval = null;
    this.locationUpdateInterval = null;
    this.upcomingEvents = [];
    this.venues = new Map();
    this.lastNotificationTime = new Map();
    
    this.options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000
    };
  }

  // Initialize with venues data
  async initialize() {
    await this.loadVenues();
  }

  // FIXED: Load all venues with proper coordinate validation
  async loadVenues() {
    try {
      const venuesSnapshot = await getDocs(collection(db, 'venues'));
      let validVenuesCount = 0;
      
      venuesSnapshot.forEach((doc) => {
        const venue = { id: doc.id, ...doc.data() };
        
        // FIXED: Validate coordinates exist and are valid numbers
        if (venue.coordinates && 
            typeof venue.coordinates.lat === 'number' && 
            typeof venue.coordinates.lng === 'number' &&
            !isNaN(venue.coordinates.lat) && 
            !isNaN(venue.coordinates.lng)) {
          
          this.venues.set(venue.id, venue);
          validVenuesCount++;
          
         // console.log(`Loaded venue: ${venue.name}`, {
         //   id: venue.id,
         //   coordinates: venue.coordinates,
        //    address: venue.address
        //  });
        } else {
         // console.warn(`Venue "${venue.name}" has invalid coordinates:`, venue.coordinates);
        }
      });
      
     // console.log(`Loaded ${validVenuesCount} venues with valid coordinates out of ${venuesSnapshot.size} total venues`);
      return validVenuesCount;
    } catch (error) {
     // console.error('Error loading venues:', error);
      return 0;
    }
  }

  // FIXED: Enhanced venue debugging
  async startMonitoring(userEvents = []) {
    //console.log('=== Starting Enhanced Location Monitoring ===');
    
    if (!this.venues.size) {
     // console.log('No venues loaded, initializing...');
      const venueCount = await this.initialize();
      if (venueCount === 0) {
     //   console.error('No venues with valid coordinates found!');
        return;
      }
    }

    this.upcomingEvents = this.filterUpcomingEvents(userEvents);
    
    if (this.upcomingEvents.length === 0) {
      //console.log('No upcoming events to monitor for check-in');
      return;
    }

    // FIXED: Debug upcoming events and their venues
   // console.log('Upcoming events for monitoring:');
    this.upcomingEvents.forEach(event => {
      const venue = this.venues.get(event.venueId);
   //   console.log(`- Event: ${event.title}`);
   //   console.log(`  Venue ID: ${event.venueId}`);
   //   console.log(`  Venue Found: ${venue ? 'YES' : 'NO'}`);
      if (venue) {
  //      console.log(`  Venue: ${venue.name}`);
   //     console.log(`  Coordinates: ${venue.coordinates.lat}, ${venue.coordinates.lng}`);
      } else {
     //   console.warn(`  WARNING: Venue not found for event ${event.title}`);
      }
    });

   // console.log(`Starting check-in monitoring for ${this.upcomingEvents.length} events`);
    this.startLocationTracking();
    this.startEventChecking();
    this.startLocationUpdates();
  }

  // Filter events that are eligible for check-in
  filterUpcomingEvents(events) {
    const now = new Date();
    const twoHoursFromNow = new Date(now.getTime() + (2 * 60 * 60 * 1000));

    return events.filter(event => {
      const eventStart = new Date(event.startDate);
      const minutesUntilStart = (eventStart - now) / (1000 * 60);
      
      const isTimeEligible = eventStart >= now && 
                            eventStart <= twoHoursFromNow && 
                            minutesUntilStart <= 120;
      
      const hasValidRegistration = event.userRegistration && 
                                  event.userRegistration.status !== 'cancelled' &&
                                  !event.userRegistration.checkedIn;

      const isConfirmedParticipant = event.participants?.includes(event.userRegistration?.userId);

      // FIXED: Also check if venue exists and has coordinates
      const hasValidVenue = event.venueId && this.venues.has(event.venueId);

      const isEligible = isTimeEligible && hasValidRegistration && isConfirmedParticipant && hasValidVenue;
      
      if (!hasValidVenue && isTimeEligible && hasValidRegistration && isConfirmedParticipant) {
      //  console.warn(`Event "${event.title}" is eligible but has no valid venue (venueId: ${event.venueId})`);
      }

      return isEligible;
    });
  }

  // Start regular location updates every 5 seconds
  startLocationUpdates() {
    this.locationUpdateInterval = setInterval(() => {
      if (this.isTracking && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const newPosition = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp
            };
            
            if (this.hasPositionChanged(newPosition)) {
              this.lastKnownPosition = newPosition;
              this.checkForCheckinOpportunities();
             // console.log('Location updated:', {
               // lat: newPosition.lat.toFixed(6),
               // lng: newPosition.lng.toFixed(6),
             //   accuracy: Math.round(newPosition.accuracy)
             // });
            }
          },
          (error) => {
            if (error.code !== error.TIMEOUT) {
             // console.warn('Location update error:', error.message);
            }
          },
          {
            ...this.options,
            timeout: 5000
          }
        );
      }
    }, 5000);
  }

  hasPositionChanged(newPosition) {
    if (!this.lastKnownPosition) return true;
    
    const distance = this.calculateDistance(
      this.lastKnownPosition.lat,
      this.lastKnownPosition.lng,
      newPosition.lat,
      newPosition.lng
    );
    
    return distance > 10;
  }

  async startLocationTracking() {
    if (!navigator.geolocation || this.isTracking) return;

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      
      if (permission.state === 'denied') {
        this.handleLocationError({
          code: 1,
          message: 'Location access denied',
          PERMISSION_DENIED: 1
        });
        return;
      }
    } catch (error) {
     // console.warn('Could not check geolocation permission:', error);
    }

    this.isTracking = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.lastKnownPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };
      //  console.log('Got initial location:', {
      //    lat: this.lastKnownPosition.lat.toFixed(6),
      //    lng: this.lastKnownPosition.lng.toFixed(6),
      //    accuracy: Math.round(this.lastKnownPosition.accuracy)
       // });
        this.checkForCheckinOpportunities();
      },
      (error) => this.handleLocationError(error),
      this.options
    );

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const newPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };
        
        if (this.hasPositionChanged(newPosition)) {
          this.lastKnownPosition = newPosition;
          this.checkForCheckinOpportunities();
        }
      },
      (error) => {
        if (error.code !== error.TIMEOUT) {
          this.handleLocationError(error);
        }
      },
      this.options
    );
  }

  stopLocationTracking() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    
    if (this.locationUpdateInterval) {
      clearInterval(this.locationUpdateInterval);
      this.locationUpdateInterval = null;
    }
    
    this.isTracking = false;
  }

  startEventChecking() {
    this.checkInterval = setInterval(() => {
      this.updateUpcomingEvents();
      this.checkForCheckinOpportunities();
    }, 30000);
  }

  stopEventChecking() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  updateUpcomingEvents() {
    const now = new Date();
    this.upcomingEvents = this.upcomingEvents.filter(event => {
      const eventStart = new Date(event.startDate);
      const minutesUntilStart = (eventStart - now) / (1000 * 60);
      return minutesUntilStart > -30;
    });
  }

  // FIXED: Enhanced proximity checking with detailed logging
  checkForCheckinOpportunities() {
    if (!this.lastKnownPosition || this.upcomingEvents.length === 0) {
      console.log('Skipping check - no location or no events');
      return;
    }

    const now = new Date();
    
    console.log('=== Checking for check-in opportunities ===');
    console.log(`User location: ${this.lastKnownPosition.lat.toFixed(6)}, ${this.lastKnownPosition.lng.toFixed(6)}`);
    console.log(`Checking ${this.upcomingEvents.length} events...`);
    
    this.upcomingEvents.forEach(event => {
      const eventStart = new Date(event.startDate);
      const minutesUntilStart = (eventStart - now) / (1000 * 60);
      
      console.log(`\nEvent: ${event.title}`);
      console.log(`Minutes until start: ${minutesUntilStart.toFixed(1)}`);
      
      if (minutesUntilStart <= 30 && minutesUntilStart > -30) {
        this.checkEventProximity(event, minutesUntilStart);
      } else {
        console.log(`Event not in check-in window (need -30 to +30 minutes)`);
      }
    });
  }

  // FIXED: Enhanced proximity checking with detailed debugging
  checkEventProximity(event, minutesUntilStart) {
    // FIXED: Get venue from cached venues with proper error handling
    const venue = this.venues.get(event.venueId);
    
    console.log(`Checking proximity for event: ${event.title}`);
    console.log(`Venue ID: ${event.venueId}`);
    
    if (!venue) {
      console.error(`No venue found for venueId: ${event.venueId}`);
      console.log('Available venues:', Array.from(this.venues.keys()));
      return;
    }

    console.log(`Venue: ${venue.name}`);
    console.log(`Venue coordinates: ${venue.coordinates.lat}, ${venue.coordinates.lng}`);

    // FIXED: Validate venue coordinates again
    if (!venue.coordinates || 
        typeof venue.coordinates.lat !== 'number' || 
        typeof venue.coordinates.lng !== 'number' ||
        isNaN(venue.coordinates.lat) || 
        isNaN(venue.coordinates.lng)) {
      console.error(`Venue "${venue.name}" has invalid coordinates:`, venue.coordinates);
      return;
    }

    const distance = this.calculateDistance(
      this.lastKnownPosition.lat,
      this.lastKnownPosition.lng,
      venue.coordinates.lat,
      venue.coordinates.lng
    );

    console.log(`Distance to venue: ${Math.round(distance)}m`);

    const maxDistance = 500; // meters
    if (distance <= maxDistance) {
      const eventKey = event.id;
      const lastNotified = this.lastNotificationTime.get(eventKey) || 0;
      const timeSinceLastNotification = Date.now() - lastNotified;
      
      console.log(`🎯 USER IS WITHIN ${maxDistance}m OF VENUE!`);
      console.log(`Time since last notification: ${timeSinceLastNotification / 1000}s`);
      
      if (timeSinceLastNotification > 300000) { // 5 minutes
        console.log(`✅ Triggering check-in opportunity for ${event.title}`);
        this.triggerCheckinOpportunity(event, venue, distance, minutesUntilStart);
        this.lastNotificationTime.set(eventKey, Date.now());
      } else {
        console.log(`⏳ Skipping notification (too soon since last one)`);
      }
    } else {
      console.log(`❌ User too far from venue (${Math.round(distance)}m > ${maxDistance}m)`);
    }
  }

  triggerCheckinOpportunity(event, venue, distance, minutesUntilStart) {
    const checkinData = {
      event: {
        ...event,
        venue: venue
      },
      venue,
      distance: Math.round(distance),
      minutesUntilStart: Math.round(minutesUntilStart),
      userLocation: this.lastKnownPosition,
      timestamp: Date.now(),
      canCheckin: this.canUserCheckin(event, minutesUntilStart)
    };

    if (checkinData.canCheckin) {
      console.log('🚀 TRIGGERING CHECK-IN OPPORTUNITY:', checkinData);
      
      this.callbacks.forEach(callback => {
        try {
          callback(checkinData);
        } catch (error) {
          console.error('Error in check-in callback:', error);
        }
      });
    } else {
      console.log('❌ User cannot check in to this event');
    }
  }

  canUserCheckin(event, minutesUntilStart) {
    if (minutesUntilStart > 30 || minutesUntilStart < -30) {
      console.log('Cannot check in: outside time window');
      return false;
    }

    if (!event.userRegistration || 
        event.userRegistration.status === 'cancelled' ||
        event.userRegistration.checkedIn) {
      console.log('Cannot check in: invalid registration status');
      return false;
    }

    if (!event.participants?.includes(event.userRegistration.userId)) {
      console.log('Cannot check in: not a confirmed participant');
      return false;
    }

    if (event.userRegistration.totalCost > 0 && 
        event.userRegistration.paymentStatus !== 'confirmed') {
      console.log('Cannot check in: payment not confirmed');
      return false;
    }

    return true;
  }

  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lng2-lng1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  }

  addCheckinCallback(callback) {
    this.callbacks.add(callback);
    
    return () => {
      this.callbacks.delete(callback);
    };
  }

  handleLocationError(error) {
    let errorMessage = 'Location access error';
    let shouldRetry = false;
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location access denied. Please enable location services to use check-in features.';
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Your location is currently unavailable. Check your GPS settings.';
        shouldRetry = true;
        break;
      case error.TIMEOUT:
        errorMessage = 'Location request timed out. Trying again...';
        shouldRetry = true;
        break;
    }
    
    console.warn('Location error for check-in:', errorMessage, error);
    
    this.callbacks.forEach(callback => {
      try {
        callback({
          type: 'location_error',
          message: errorMessage,
          error: error,
          shouldRetry: shouldRetry
        });
      } catch (callbackError) {
        console.error('Error in location error callback:', callbackError);
      }
    });

    if (shouldRetry) {
      setTimeout(() => {
        if (this.isTracking) {
          console.log('Retrying location access...');
          this.startLocationTracking();
        }
      }, 30000);
    }
  }

  updateEvents(newEvents) {
    const oldCount = this.upcomingEvents.length;
    this.upcomingEvents = this.filterUpcomingEvents(newEvents);
    
    if (this.upcomingEvents.length !== oldCount) {
      console.log(`Events updated: ${this.upcomingEvents.length} upcoming events`);
    }
    
    if (this.upcomingEvents.length === 0) {
      this.stopMonitoring();
    }
  }

  stopMonitoring() {
    this.stopLocationTracking();
    this.stopEventChecking();
    this.callbacks.clear();
    this.lastNotificationTime.clear();
   // console.log('Location monitoring stopped');
  }

  // ENHANCED: Better debugging status
  getStatus() {
    return {
      isTracking: this.isTracking,
      hasLocation: !!this.lastKnownPosition,
      eventsCount: this.upcomingEvents.length,
      venuesCount: this.venues.size,
      lastLocationTime: this.lastKnownPosition?.timestamp,
      accuracy: this.lastKnownPosition?.accuracy,
      location: this.lastKnownPosition ? {
        lat: this.lastKnownPosition.lat.toFixed(6),
        lng: this.lastKnownPosition.lng.toFixed(6)
      } : null,
      venues: Array.from(this.venues.values()).map(v => ({
        id: v.id,
        name: v.name,
        coordinates: v.coordinates
      })),
      upcomingEvents: this.upcomingEvents.map(e => ({
        id: e.id,
        title: e.title,
        venueId: e.venueId,
        hasVenue: this.venues.has(e.venueId)
      }))
    };
  }

  async getLocationPermissionStatus() {
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      return {
        state: permission.state,
        canRequest: permission.state === 'prompt'
      };
    } catch (error) {
      return {
        state: 'unknown',
        canRequest: true
      };
    }
  }

  async requestLocationPermission() {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            granted: true,
            position: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy
            }
          });
        },
        (error) => {
          resolve({
            granted: false,
            error: error
          });
        },
        { timeout: 10000 }
      );
    });
  }
}

// Enhanced React hook - same as before but with better debugging
export function useEnhancedCheckinLocationDetection() {
  const [checkinOpportunity, setCheckinOpportunity] = useState(null);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [locationPermission, setLocationPermission] = useState('unknown');
  const [serviceStatus, setServiceStatus] = useState(null);
  const serviceRef = useRef(null);

  useEffect(() => {
    serviceRef.current = new EnhancedCheckinLocationService();
    
    const unsubscribe = serviceRef.current.addCheckinCallback((data) => {
      if (data.type === 'location_error') {
        setLocationError(data.message);
        setLocationPermission('denied');
      } else {
        setCheckinOpportunity(data);
        setLocationError(null);
      }
    });

    checkLocationPermission();

    const statusInterval = setInterval(() => {
      if (serviceRef.current) {
        setServiceStatus(serviceRef.current.getStatus());
      }
    }, 10000);

    return () => {
      if (serviceRef.current) {
        serviceRef.current.stopMonitoring();
      }
      clearInterval(statusInterval);
      unsubscribe();
    };
  }, []);

  const checkLocationPermission = async () => {
    if (serviceRef.current) {
      const status = await serviceRef.current.getLocationPermissionStatus();
      setLocationPermission(status.state);
    }
  };

  const requestLocationPermission = useCallback(async () => {
    if (serviceRef.current) {
      const result = await serviceRef.current.requestLocationPermission();
      if (result.granted) {
        setLocationPermission('granted');
        setLocationError(null);
        return true;
      } else {
        setLocationPermission('denied');
        setLocationError('Location access is required for automatic check-in features');
        return false;
      }
    }
    return false;
  }, []);

  const startMonitoring = useCallback(async (userEvents) => {
    if (!serviceRef.current) return;

    try {
      setLocationError(null);
      await serviceRef.current.startMonitoring(userEvents);
      setIsMonitoring(true);
    } catch (error) {
      setLocationError(error.message);
      console.error('Error starting check-in monitoring:', error);
    }
  }, []);

  const stopMonitoring = useCallback(() => {
    if (serviceRef.current) {
      serviceRef.current.stopMonitoring();
      setIsMonitoring(false);
      setCheckinOpportunity(null);
    }
  }, []);

  const updateEvents = useCallback((events) => {
    if (serviceRef.current) {
      serviceRef.current.updateEvents(events);
    }
  }, []);

  const dismissCheckinOpportunity = useCallback(() => {
    setCheckinOpportunity(null);
  }, []);

  const getServiceStatus = useCallback(() => {
    return serviceStatus;
  }, [serviceStatus]);

  return {
    checkinOpportunity,
    isMonitoring,
    locationError,
    locationPermission,
    serviceStatus,
    startMonitoring,
    stopMonitoring,
    updateEvents,
    dismissCheckinOpportunity,
    requestLocationPermission,
    getServiceStatus
  };
}